/**
 * A range input whose track shows how far along it is.
 *
 * A native `<input type="range">` draws ONE uniform track. There is no cross-browser pseudo
 * element for the portion behind the thumb — Firefox has `::-moz-range-progress`, WebKit has
 * nothing — so a slider meant to read as a progress bar has to paint its own fill, and CSS
 * cannot do it because CSS cannot read an input's value. Hence a gradient whose stop is a custom
 * property, and this, which is the only thing that knows what to put in it.
 *
 * Painted from four places, because the fill is lost or wrong in four different ways:
 *
 *   - the DRAG. `input` fires per pixel, which is the whole point: a fill that only catches up
 *     on release is the first bug this fixed.
 *   - the NUMBER BOX beside it, which writes the same Livewire property. No `input` event ever
 *     reaches the slider then, so the fill would sit where the thumb used to be. `$wire.$watch`
 *     is how the rest of this codebase hears about that (see `richText`), and it takes the
 *     property PATH as a string, so it works for `progress` and for `form.data.progress` alike —
 *     which Alpine's own reactivity would not, since `$wire.form.data.progress` is member access
 *     and cannot express an array index.
 *   - any LIVEWIRE RE-RENDER. This is the second bug, and the least obvious: the fill lives in an
 *     inline custom property, and the server sends this input with no `style` attribute at all —
 *     so the morph patches the live node to match and REMOVES it, dropping the bar back to the
 *     stylesheet's 0%. Moving a card to another state blanked the slider in the panel beside it
 *     for exactly that reason, and so would a toast, a filter, or anything else that re-rendered
 *     the panel. The value survives the morph, so the percentage is still knowable; it just has
 *     to be drawn again, which is what the style observer does.
 *   - the FIRST RENDER, which is none of those.
 */
/**
 * The observers, kept OUT of Alpine's reach.
 *
 * Alpine wraps a component's data in a reactive proxy, and anything it does not recognise as a
 * plain object it leaves alone — but a MutationObserver is only recognised by its `toStringTag`,
 * and not every DOM implementation gives it one. Where it does not, storing the observer on the
 * component means `observe()` is invoked with the PROXY as its receiver, the registration never
 * takes effect, and nothing errors: the observer simply never fires. A WeakMap keyed by the
 * element keeps it off the reactive object in every implementation, and releases it with the node.
 */
const observers = new WeakMap();

export function rangeFill(property = null) {
    return {
        /**
         * The input, captured once.
         *
         * `$el` is the element of the CURRENT evaluation context, not the component root. Every
         * handler here sits on the input itself, so it would happen to be right — but the board's
         * resize handle lost an afternoon to exactly that assumption, and capturing it costs
         * nothing next to remembering when it holds.
         */
        el: null,

        init() {
            this.el = this.$el;
            this.adoptTheStoredValue();
            this.paint();

            // Guarded rather than assumed: this component is also used outside a Livewire
            // component, where there is no $wire at all, and a drag-only slider is still correct.
            if (property && this.$wire && typeof this.$wire.$watch === 'function') {
                this.$wire.$watch(property, () => this.paint());
            }

            this.watchForTheStyleBeingWipedOut();


        },

        /**
         * Open on the stored number, not on the DOM's idea of one.
         *
         * The server renders this input with no `value` attribute, and a range input without one
         * does not default to its minimum — it defaults to the MIDPOINT of its range. So a task
         * at 40% arrives with the thumb at 50, and Livewire's own sync sets `el.value` without
         * firing `input` or touching the style, so nothing would repaint it afterwards.
         *
         * Reading the property here settles it before anything is drawn. Only at init, and only
         * when there is something stored: once somebody is dragging, the DOM is ahead of the
         * server and the DOM is right.
         */
        adoptTheStoredValue() {
            if (! property || ! this.el || ! this.$wire || typeof this.$wire.get !== 'function') {
                return;
            }

            const stored = this.$wire.get(property);

            if (stored !== null && stored !== undefined && stored !== '') {
                this.el.value = stored;
            }
        },

        /**
         * Redraw whenever something removes the fill.
         *
         * A MutationObserver rather than a Livewire hook because it needs no Livewire at all:
         * whatever wipes the property — a morph, another script, the element being reset — the
         * answer is the same, and this component is used on screens that are not Livewire's.
         *
         * It fires on the property being ABSENT, never on its value, so the write inside `paint`
         * cannot feed the observer that watches it. Attribute mutations are delivered at the end
         * of the microtask, by which point a morph has finished setting the value too — so the
         * repaint uses the number the re-render brought with it rather than the one on screen.
         */
        watchForTheStyleBeingWipedOut() {
            if (typeof MutationObserver !== 'function' || ! this.el) {
                return;
            }

            const observer = new MutationObserver(() => {
                if (this.el.style.getPropertyValue('--zar-range-pct') === '') {
                    this.paint();
                }
            });

            // Registered through the raw instance, never through `this`. @see the note on `observers`
            observer.observe(this.el, { attributes: true, attributeFilter: ['style'] });
            observers.set(this.el, observer);
        },

        destroy() {
            observers.get(this.el)?.disconnect();
            observers.delete(this.el);
        },

        /** Where the thumb sits along the TRACK, as a percentage — which is not the value. */
        percent() {
            const el = this.el;
            if (! el) {
                return 0;
            }

            const min = toNumber(el.min, 0);
            const max = toNumber(el.max, 100);
            const value = toNumber(el.value, min);

            // A zero-width range has no "along" to report, and NaN in a gradient draws no fill
            // at all rather than reporting an error.
            if (max === min) {
                return 0;
            }

            return clamp(((value - min) / (max - min)) * 100);
        },

        paint() {
            if (this.el) {
                this.el.style.setProperty('--zar-range-pct', this.percent() + '%');
            }
        },
    };
}

function toNumber(value, fallback) {
    const parsed = parseFloat(value);

    return Number.isFinite(parsed) ? parsed : fallback;
}

/**
 * Inside the track, and to a number a stylesheet can hold.
 *
 * Float division does not land on round figures — 55/100 as a percentage is 55.00000000000001,
 * which goes into the gradient verbatim. Two decimals is finer than any screen can draw and
 * keeps the value readable in devtools.
 */
function clamp(percent) {
    return Math.round(Math.max(0, Math.min(100, percent)) * 100) / 100;
}
